import { useEffect, useRef, useState } from "react";
import {
  AdvaLogo,
  AdvaMark,
  AdvaWordmark,
} from "@adva/brand-assets/components";

type ColorScope = "mode" | "stable";

interface ColorSpec {
  name: string;
  cssVar: string;
  scope: ColorScope;
}

// Mode-aware palette flips between dark and light themes.
const MODE_COLORS: ColorSpec[] = [
  { name: "bg", cssVar: "--color-bg", scope: "mode" },
  { name: "surface", cssVar: "--color-surface", scope: "mode" },
  { name: "elevated", cssVar: "--color-elevated", scope: "mode" },
  { name: "border", cssVar: "--color-border", scope: "mode" },
  { name: "text-hi", cssVar: "--color-text-hi", scope: "mode" },
  { name: "text-md", cssVar: "--color-text-md", scope: "mode" },
  { name: "text-lo", cssVar: "--color-text-lo", scope: "mode" },
  { name: "accent", cssVar: "--color-accent", scope: "mode" },
  { name: "accent-hi", cssVar: "--color-accent-hi", scope: "mode" },
  { name: "success", cssVar: "--color-success", scope: "mode" },
  { name: "warning", cssVar: "--color-warning", scope: "mode" },
  { name: "danger", cssVar: "--color-danger", scope: "mode" },
];

// Theme-stable tokens never flip — used when placement background is fixed
// (solid-tile brand marks, OG cards).
const STABLE_COLORS: ColorSpec[] = [
  { name: "brand-on-dark", cssVar: "--color-brand-on-dark", scope: "stable" },
  { name: "brand-on-light", cssVar: "--color-brand-on-light", scope: "stable" },
  { name: "brand-bg-dark", cssVar: "--color-brand-bg-dark", scope: "stable" },
  { name: "brand-bg-light", cssVar: "--color-brand-bg-light", scope: "stable" },
];

const SPACING_TOKENS = [
  "0-5",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "8",
  "10",
  "12",
  "16",
  "20",
  "24",
] as const;

const RADIUS_TOKENS = ["none", "sm", "md", "lg", "full"] as const;

const MOTION_DURATIONS = ["fast", "base", "slow"] as const;

const BRAND_COLORS = ["dark", "light", "accent"] as const;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-title font-light text-text-hi mb-8">
      {children}
    </h2>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-body text-label uppercase tracking-label text-text-lo">
      {children}
    </p>
  );
}

function useTokenValue(
  ref: React.RefObject<HTMLElement | null>,
  cssVar: string,
) {
  const [value, setValue] = useState("");
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const v = getComputedStyle(el).getPropertyValue(cssVar).trim();
    setValue(v || "—");
  }, [ref, cssVar]);
  return value;
}

function TokenValue({
  scopeRef,
  cssVar,
  className,
}: {
  scopeRef: React.RefObject<HTMLElement | null>;
  cssVar: string;
  className?: string;
}) {
  const value = useTokenValue(scopeRef, cssVar);
  return (
    <span className={className ?? "font-mono text-label text-text-lo"}>
      {value}
    </span>
  );
}

function HexLabel({
  scopeRef,
  cssVar,
}: {
  scopeRef: React.RefObject<HTMLElement | null>;
  cssVar: string;
}) {
  const value = useTokenValue(scopeRef, cssVar);
  return (
    <span
      data-testid={`hex-${cssVar}`}
      className="font-mono text-label uppercase text-text-md"
    >
      {value}
    </span>
  );
}

function Swatch({
  spec,
  scopeRef,
}: {
  spec: ColorSpec;
  scopeRef: React.RefObject<HTMLElement | null>;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-20 w-full rounded-md border border-border"
        style={{ background: `var(${spec.cssVar})` }}
        aria-hidden
      />
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-data text-text-hi">{spec.name}</span>
        <HexLabel scopeRef={scopeRef} cssVar={spec.cssVar} />
      </div>
      <code className="font-mono text-label text-text-lo">{spec.cssVar}</code>
    </div>
  );
}

const TYPE_SCALE: {
  token: string;
  family: "display" | "body" | "mono";
  weight: "light" | "regular" | "medium" | "semibold";
  sample: string;
  description: string;
  extras?: string;
}[] = [
  {
    token: "display",
    family: "display",
    weight: "light",
    sample: "Adva",
    description: "Hero moments",
  },
  {
    token: "title",
    family: "display",
    weight: "light",
    sample: "Section title",
    description: "Section headings",
  },
  {
    token: "heading",
    family: "body",
    weight: "semibold",
    sample: "Subheading or card title",
    description: "Card & subheadings",
  },
  {
    token: "body",
    family: "body",
    weight: "regular",
    sample: "Body copy — the default reading size for prose and UI.",
    description: "Default body",
  },
  {
    token: "label",
    family: "body",
    weight: "medium",
    sample: "Label · metadata",
    description: "Uppercase labels",
    extras: "uppercase",
  },
  {
    token: "data",
    family: "mono",
    weight: "regular",
    sample: "$1,234.56 · 0000-0000",
    description: "Tabular / code",
    extras: "tabular-nums",
  },
];

function TypeScaleRow({
  token,
  family,
  weight,
  sample,
  description,
  extras,
  scopeRef,
}: {
  token: string;
  family: "display" | "body" | "mono";
  weight: "light" | "regular" | "medium" | "semibold";
  sample: string;
  description: string;
  extras?: string;
  scopeRef: React.RefObject<HTMLDivElement | null>;
}) {
  const familyClass = `font-${family}`;
  const weightClass = `font-${weight}`;
  const sizeClass = `text-${token}`;
  const trackingClass =
    token === "display"
      ? "tracking-display"
      : token === "label"
        ? "tracking-label uppercase"
        : "";

  const fontSize = useTokenValue(scopeRef, `--font-size-${token}`);
  const lineHeight = useTokenValue(scopeRef, `--line-height-${token}`);
  const letterSpacing = useTokenValue(scopeRef, `--letter-spacing-${token}`);
  const fontFamily = useTokenValue(scopeRef, `--font-family-${family}`);
  const fontWeight = useTokenValue(scopeRef, `--font-weight-${weight}`);

  const familyShort = fontFamily.split(",")[0]?.replace(/"/g, "").trim();

  return (
    <div className="border-b border-border py-6 last:border-b-0">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[200px_1fr]">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-data text-text-hi">{token}</span>
          <span className="font-mono text-label text-text-lo">
            {description}
          </span>
        </div>
        <div
          className={`${familyClass} ${weightClass} ${sizeClass} ${trackingClass} text-text-hi`}
        >
          {sample}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 pl-0 md:pl-[200px]">
        <span className="font-mono text-label text-text-lo">
          {familyShort} {fontWeight}
        </span>
        <span className="font-mono text-label text-text-lo">
          {fontSize} / {lineHeight}
        </span>
        {letterSpacing && letterSpacing !== "—" && (
          <span className="font-mono text-label text-text-lo">
            tracking {letterSpacing}
          </span>
        )}
        {extras && (
          <span className="font-mono text-label text-text-lo">{extras}</span>
        )}
      </div>
    </div>
  );
}

function FontSample({
  family,
  weight,
  label,
}: {
  family: "display" | "body" | "mono";
  weight: "light" | "regular" | "medium" | "semibold";
  label: string;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-border py-4 last:border-b-0">
      <Label>{label}</Label>
      <span
        className={`font-${family} font-${weight} text-heading text-text-hi`}
      >
        The quick brown fox jumps over the lazy dog 0123456789
      </span>
    </div>
  );
}

function tileClasses(color: "dark" | "light" | "accent") {
  if (color === "dark" || color === "accent")
    return "rounded-md border border-border bg-brand-bg-dark";
  return "rounded-md border border-border bg-elevated";
}

function tileTheme(color: "dark" | "light" | "accent") {
  return color === "dark" || color === "accent" ? "dark" : undefined;
}

function MarkTile({
  size,
  color,
  variant,
}: {
  size: number;
  color: "dark" | "light" | "accent";
  variant: "transparent" | "solid";
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        data-theme={tileTheme(color)}
        className={`flex h-32 w-32 items-center justify-center ${tileClasses(color)}`}
      >
        <AdvaMark size={size} color={color} variant={variant} />
      </div>
      <span className="font-mono text-label text-text-lo">{size}px</span>
    </div>
  );
}

function LogoTile({
  size,
  layout,
  color,
  variant,
}: {
  size: number;
  layout: "horizontal" | "stacked";
  color: "dark" | "light" | "accent";
  variant: "transparent" | "solid";
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        data-theme={tileTheme(color)}
        className={`flex h-40 items-center justify-center p-4 ${tileClasses(color)}`}
        style={{ minWidth: "16rem" }}
      >
        <AdvaLogo size={size} layout={layout} color={color} variant={variant} />
      </div>
      <span className="font-mono text-label text-text-lo">
        {layout} · {size}px
      </span>
    </div>
  );
}

function StatusDot({
  color,
  label,
}: {
  color: "success" | "warning" | "danger" | "accent";
  label: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: `var(--color-${color})` }}
        aria-hidden
      />
      <span className="font-body text-body text-text-md">{label}</span>
    </div>
  );
}

function DataPill({
  color,
  label,
}: {
  color: "success" | "warning" | "danger";
  label: string;
}) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-3 py-1 font-mono text-label uppercase tracking-label"
      style={{
        borderColor: `var(--color-${color})`,
        color: `var(--color-${color})`,
      }}
    >
      {label}
    </span>
  );
}

export default function BrandPage() {
  const [theme, setTheme] = useState<"dark" | "light">("dark");

  return (
    <div
      className="min-h-screen bg-bg text-text-hi font-body"
      data-theme={theme}
    >
      <div
        key={theme}
        className="mx-auto max-w-6xl px-6 py-16 md:px-10 md:py-24"
      >
        {/* ─────────────────────────── Hero ─────────────────────────── */}
        <section className="mb-24 flex flex-col items-start gap-8">
          <div className="flex w-full items-start justify-between">
            <AdvaLogo
              layout="horizontal"
              size={128}
              color={theme === "dark" ? "dark" : "light"}
            />
            <div className="flex items-center gap-1 rounded-lg border border-border bg-surface p-1">
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={`rounded-md px-3 py-1.5 font-body text-label uppercase tracking-label transition-colors ${
                  theme === "dark"
                    ? "bg-elevated text-text-hi"
                    : "text-text-lo hover:text-text-md"
                }`}
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={`rounded-md px-3 py-1.5 font-body text-label uppercase tracking-label transition-colors ${
                  theme === "light"
                    ? "bg-elevated text-text-hi"
                    : "text-text-lo hover:text-text-md"
                }`}
              >
                Light
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-mono text-label uppercase tracking-label text-accent">
              Design system · Living reference
            </p>
            <h1 className="font-display text-display font-light tracking-display text-text-hi">
              Adva brand foundation
            </h1>
            <p className="font-body text-heading text-text-md max-w-2xl">
              Tokens, type, and marks. Everything on this page reads from{" "}
              <code className="font-mono text-accent">@adva/tokens</code> and
              the brand components. Change a token → this page updates itself.
            </p>
          </div>
        </section>

        {/* ─────────────────────────── Colors ─────────────────────────── */}
        <ColorSection />

        {/* ────────────────────────── Typography ────────────────────────── */}
        <TypeSection />

        {/* ────────────────────────── Spacing ────────────────────────── */}
        <SpacingSection />

        {/* ──────────────────────── Border radius ──────────────────────── */}
        <RadiusSection />

        {/* ──────────────────────── Motion ──────────────────────── */}
        <MotionSection />

        {/* ─────────────────────────── Logo ─────────────────────────── */}
        <section className="mb-24">
          <SectionTitle>Logo</SectionTitle>
          <p className="font-body text-body text-text-md mb-10 max-w-2xl">
            Mark, wordmark, and lockup variants rendered from the React brand
            components. Dark ink sits on light tiles; light ink sits on dark
            tiles; accent takes the dark tile.
          </p>

          <div className="mb-8">
            <Label>Favicons</Label>
          </div>
          <div className="mb-12 flex flex-wrap items-end gap-6 rounded-lg border border-border bg-surface p-6">
            <div className="flex flex-col items-center gap-2">
              <img
                src="/favicon-16.png"
                alt="Favicon 16×16"
                width={16}
                height={16}
              />
              <span className="font-mono text-label text-text-lo">16px</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <img
                src="/favicon-32.png"
                alt="Favicon 32×32"
                width={32}
                height={32}
              />
              <span className="font-mono text-label text-text-lo">32px</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <img
                src="/apple-touch-icon.png"
                alt="Apple touch icon"
                width={64}
                height={64}
              />
              <span className="font-mono text-label text-text-lo">
                apple-touch 180px
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <img
                src="/favicon.svg"
                alt="Favicon SVG"
                width={48}
                height={48}
              />
              <span className="font-mono text-label text-text-lo">svg</span>
            </div>
          </div>

          <div className="mb-4">
            <Label>Mark · transparent (dark / light / accent)</Label>
          </div>
          <div className="mb-10 grid grid-cols-3 gap-6 md:grid-cols-6">
            {BRAND_COLORS.map((color) => (
              <MarkTile
                key={`mark-${color}-48`}
                size={48}
                color={color}
                variant="transparent"
              />
            ))}
            {BRAND_COLORS.map((color) => (
              <MarkTile
                key={`mark-${color}-64`}
                size={64}
                color={color}
                variant="transparent"
              />
            ))}
          </div>

          <div className="mb-4">
            <Label>Mark · solid tiles (dark / light / accent)</Label>
          </div>
          <div className="mb-10 grid grid-cols-3 gap-6">
            <MarkTile size={96} color="dark" variant="solid" />
            <MarkTile size={96} color="light" variant="solid" />
            <MarkTile size={96} color="accent" variant="solid" />
          </div>

          <div className="mb-4">
            <Label>Wordmark</Label>
          </div>
          <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-brand-bg-dark p-8">
              <AdvaWordmark size={32} color="dark" />
              <span className="font-mono text-label text-brand-on-dark opacity-60">
                dark · 32px
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-elevated p-8">
              <AdvaWordmark size={32} color="light" />
              <span className="font-mono text-label text-text-lo">
                light · 32px
              </span>
            </div>
            <div
              data-theme="dark"
              className="flex flex-col items-center gap-2 rounded-md border border-border bg-brand-bg-dark p-8"
            >
              <AdvaWordmark size={32} color="accent" />
              <span className="font-mono text-label text-brand-on-dark opacity-60">
                accent · 32px
              </span>
            </div>
          </div>

          <div className="mb-4">
            <Label>Lockup · horizontal (dark / light / accent)</Label>
          </div>
          <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            <LogoTile
              size={48}
              layout="horizontal"
              color="dark"
              variant="transparent"
            />
            <LogoTile
              size={48}
              layout="horizontal"
              color="light"
              variant="transparent"
            />
            <LogoTile
              size={48}
              layout="horizontal"
              color="accent"
              variant="transparent"
            />
          </div>

          <div className="mb-4">
            <Label>Lockup · stacked (dark / light / accent)</Label>
          </div>
          <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            <LogoTile
              size={96}
              layout="stacked"
              color="dark"
              variant="transparent"
            />
            <LogoTile
              size={96}
              layout="stacked"
              color="light"
              variant="transparent"
            />
            <LogoTile
              size={96}
              layout="stacked"
              color="accent"
              variant="transparent"
            />
          </div>

          <div className="mb-4">
            <Label>Accent on light vs dark</Label>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col items-center gap-2">
              <div
                data-theme="light"
                className="flex h-40 w-full items-center justify-center rounded-md border border-border bg-elevated p-4"
              >
                <AdvaLogo
                  size={48}
                  layout="horizontal"
                  color="accent"
                  variant="transparent"
                />
              </div>
              <span className="font-mono text-label text-text-lo">
                accent on light
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div
                data-theme="dark"
                className="flex h-40 w-full items-center justify-center rounded-md border border-border bg-brand-bg-dark p-4"
              >
                <AdvaLogo
                  size={48}
                  layout="horizontal"
                  color="accent"
                  variant="transparent"
                />
              </div>
              <span className="font-mono text-label text-text-lo">
                accent on dark
              </span>
            </div>
          </div>
        </section>

        {/* ────────────────────── Chart palette ────────────────────── */}
        <ChartSection />

        {/* ────────────────────── Accent in context ────────────────────── */}
        <section className="mb-24">
          <SectionTitle>Accent in context</SectionTitle>
          <p className="font-body text-body text-text-md mb-10 max-w-2xl">
            The accent is reserved for interaction affordances — focus rings,
            primary actions, links, status. Status colors appear only on data.
          </p>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-surface p-6">
              <Label>Focus ring &amp; outline CTA</Label>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  className="rounded-md border border-accent px-4 py-2 font-body text-body font-medium text-accent hover:bg-accent hover:text-bg focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg"
                >
                  Primary action
                </button>
                <a
                  href="#brand-hero"
                  className="font-body text-body text-accent underline decoration-accent-hi underline-offset-4 hover:text-accent-hi"
                >
                  Anchor link
                </a>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-6">
              <Label>Status dots</Label>
              <div className="mt-6 flex flex-col gap-3">
                <StatusDot color="success" label="Paid · sync up to date" />
                <StatusDot color="warning" label="Pending · review required" />
                <StatusDot color="danger" label="Failed · needs attention" />
                <StatusDot color="accent" label="Active · focused" />
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-6 md:col-span-2">
              <Label>Data pills</Label>
              <div className="mt-6 flex flex-wrap gap-3">
                <DataPill color="success" label="paid" />
                <DataPill color="warning" label="pending" />
                <DataPill color="danger" label="overdue" />
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────────────── Footer ─────────────────────────── */}
        <footer className="mt-24 flex flex-col gap-2 border-t border-border pt-8">
          <p className="font-mono text-label uppercase tracking-label text-text-lo">
            Tokens · Components · Living reference
          </p>
          <p className="font-body text-body text-text-md">
            This page is rendered from{" "}
            <code className="font-mono text-accent">@adva/tokens</code> and{" "}
            <code className="font-mono text-accent">
              packages/brand-assets/src/components
            </code>
            . A token change here is a brand update everywhere.
          </p>
        </footer>
      </div>
    </div>
  );
}

const CHART_RING = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"] as const;
const CHART_RAMP = ["ramp-1", "ramp-2", "ramp-3", "ramp-4", "ramp-5"] as const;

function ChartSection() {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section ref={ref} className="mb-24">
      <SectionTitle>Chart palette</SectionTitle>
      <p className="font-body text-body text-text-md mb-10 max-w-2xl">
        An 8-color categorical ring derived from the accent hue, rotating 45°
        per step in OKLCH. A 5-stop luminance ramp of the accent hue for ordered
        data like aging buckets.
      </p>

      <div className="mb-6">
        <Label>Categorical ring</Label>
      </div>
      <div className="mb-10 rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            {CHART_RING.map((token) => (
              <div key={token} className="flex-1">
                <div
                  className="h-12 w-full rounded-sm"
                  style={{ background: `var(--color-chart-${token})` }}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            {CHART_RING.map((token) => (
              <div
                key={token}
                className="flex-1 text-center font-mono text-label text-text-lo"
              >
                <HexLabel scopeRef={ref} cssVar={`--color-chart-${token}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <Label>Luminance ramp</Label>
      </div>
      <div className="mb-10 rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-3">
          <div className="flex gap-1">
            {CHART_RAMP.map((token) => (
              <div key={token} className="flex-1">
                <div
                  className="h-12 w-full"
                  style={{ background: `var(--color-chart-${token})` }}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            {CHART_RAMP.map((token) => (
              <div
                key={token}
                className="flex-1 text-center font-mono text-label text-text-lo"
              >
                <HexLabel scopeRef={ref} cssVar={`--color-chart-${token}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <Label>Diverging (semantic)</Label>
      </div>
      <div className="flex gap-1 rounded-lg border border-border bg-surface p-6">
        <div className="flex-1">
          <div
            className="h-12 w-full rounded-l-sm"
            style={{ background: "var(--color-success)" }}
          />
          <p className="mt-2 text-center font-mono text-label text-text-lo">
            success
          </p>
        </div>
        <div className="flex-1">
          <div
            className="h-12 w-full"
            style={{ background: "var(--color-text-lo)" }}
          />
          <p className="mt-2 text-center font-mono text-label text-text-lo">
            text-lo
          </p>
        </div>
        <div className="flex-1">
          <div
            className="h-12 w-full rounded-r-sm"
            style={{ background: "var(--color-danger)" }}
          />
          <p className="mt-2 text-center font-mono text-label text-text-lo">
            danger
          </p>
        </div>
      </div>
    </section>
  );
}

function StableColorGrid() {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className="grid grid-cols-2 gap-5 sm:grid-cols-4">
      {STABLE_COLORS.map((c) => (
        <Swatch key={c.name} spec={c} scopeRef={ref} />
      ))}
    </div>
  );
}

function ColorSection() {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section ref={ref} className="mb-24">
      <SectionTitle>Colors</SectionTitle>
      <p className="font-body text-body text-text-md mb-10 max-w-2xl">
        The mode-aware palette flips between dark and light themes. Use the
        toggle above to switch. The theme-stable tokens never flip — they're
        used when placement background is fixed, like solid-tile brand marks or
        OG cards.
      </p>

      <div className="mb-8 flex flex-col gap-6">
        <Label>Mode-aware palette</Label>
        <div className="rounded-lg border border-border bg-surface p-6">
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4">
            {MODE_COLORS.map((c) => (
              <Swatch key={c.name} spec={c} scopeRef={ref} />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <Label>Theme-stable brand tokens</Label>
        <div className="rounded-lg border border-border bg-elevated p-6">
          <StableColorGrid />
        </div>
      </div>
    </section>
  );
}

function TypeSection() {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section ref={ref} className="mb-24">
      <SectionTitle>Typography</SectionTitle>
      <p className="font-body text-body text-text-md mb-10 max-w-2xl">
        Space Grotesk for display, Inter for body UI, Geist Mono for data and
        code. All sizes, weights, and leading read from tokens.
      </p>

      <div className="mb-10 rounded-lg border border-border bg-surface p-8">
        {TYPE_SCALE.map((row) => (
          <TypeScaleRow key={row.token} {...row} scopeRef={ref} />
        ))}
      </div>

      <div className="mb-6">
        <Label>Font families + weights</Label>
      </div>
      <div className="rounded-lg border border-border bg-surface p-8">
        <FontSample family="display" weight="light" label="Space Grotesk 300" />
        <FontSample
          family="display"
          weight="medium"
          label="Space Grotesk 500"
        />
        <FontSample family="body" weight="regular" label="Inter 400" />
        <FontSample family="body" weight="medium" label="Inter 500" />
        <FontSample family="body" weight="semibold" label="Inter 600" />
        <FontSample family="mono" weight="regular" label="Geist Mono 400" />
        <FontSample family="mono" weight="medium" label="Geist Mono 500" />
      </div>

      <div className="mt-10 mb-6">
        <Label>OpenType features (global)</Label>
      </div>
      <div className="rounded-lg border border-border bg-surface p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {(
            [
              ['"rlig" 1', "Required ligatures"],
              ['"calt" 1', "Contextual alternates"],
              ['"tnum" 1', "Tabular (fixed-width) numerals"],
            ] as const
          ).map(([feature, desc]) => (
            <div key={feature} className="flex items-baseline gap-3">
              <code className="font-mono text-data text-accent">{feature}</code>
              <span className="font-mono text-label text-text-lo">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SpacingSection() {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section ref={ref} className="mb-24">
      <SectionTitle>Spacing</SectionTitle>
      <p className="font-body text-body text-text-md mb-10 max-w-2xl">
        A 4px base grid. Each step is a named token used throughout layouts,
        gaps, and padding.
      </p>
      <div className="rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-3">
          {SPACING_TOKENS.map((token) => (
            <div key={token} className="flex items-center gap-4">
              <span className="w-24 flex-shrink-0 font-mono text-label text-text-md">
                --spacing-{token}
              </span>
              <div
                className="h-4 rounded-sm bg-accent opacity-60"
                style={{ width: `var(--spacing-${token})` }}
              />
              <TokenValue scopeRef={ref} cssVar={`--spacing-${token}`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RadiusSection() {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section ref={ref} className="mb-24">
      <SectionTitle>Border radius</SectionTitle>
      <p className="font-body text-body text-text-md mb-10 max-w-2xl">
        Five radius tokens from sharp to fully round. Cards use{" "}
        <code className="font-mono text-accent">md</code>, pills use{" "}
        <code className="font-mono text-accent">full</code>.
      </p>
      <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-5">
        {RADIUS_TOKENS.map((token) => (
          <div
            key={token}
            className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface p-6"
          >
            <div
              className="h-16 w-16 bg-accent opacity-50"
              style={{ borderRadius: `var(--radius-${token})` }}
            />
            <div className="text-center">
              <span className="block font-mono text-data text-text-hi">
                {token}
              </span>
              <TokenValue scopeRef={ref} cssVar={`--radius-${token}`} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MotionSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  return (
    <section ref={ref} className="mb-24">
      <SectionTitle>Motion</SectionTitle>
      <p className="font-body text-body text-text-md mb-10 max-w-2xl">
        Three duration tiers and one easing curve. Productive motion is fast and
        purposeful — no decorative animation.
      </p>

      <div className="mb-6">
        <Label>Duration</Label>
      </div>
      <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-3">
        {MOTION_DURATIONS.map((token) => (
          <button
            key={token}
            type="button"
            onClick={() => {
              setActiveDemo(null);
              requestAnimationFrame(() => setActiveDemo(token));
            }}
            className="flex flex-col items-center gap-4 rounded-lg border border-border bg-surface p-6 hover:border-accent"
          >
            <div className="relative h-8 w-full overflow-hidden rounded-sm bg-elevated">
              <div
                className={`absolute left-0 top-0 h-full rounded-sm bg-accent ${activeDemo === token ? "w-full" : "w-0"}`}
                style={{
                  transitionProperty: "width",
                  transitionDuration: `var(--motion-duration-${token})`,
                  transitionTimingFunction: "var(--motion-easing-standard)",
                }}
              />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="font-mono text-data text-text-hi">{token}</span>
              <TokenValue
                scopeRef={ref}
                cssVar={`--motion-duration-${token}`}
              />
            </div>
          </button>
        ))}
      </div>

      <div className="mb-6">
        <Label>Easing</Label>
      </div>
      <div className="rounded-lg border border-border bg-surface p-6">
        <div className="flex items-baseline gap-4">
          <code className="font-mono text-data text-text-hi">standard</code>
          <TokenValue scopeRef={ref} cssVar="--motion-easing-standard" />
        </div>
        <p className="mt-2 font-body text-body text-text-lo">
          Decelerate curve — fast start, soft stop. Applied to all UI
          transitions by default.
        </p>
      </div>
    </section>
  );
}
